/** @prettier */

import React, {useState, useEffect} from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {listTimeZones} from 'timezone-support';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
import FindLocation from './FindLocation';
import ComboBox from '../../component/AutoCompleteBox';
import TextFieldSelectBox from '../../component/SelectBox';
import DialogPage from '../../component/DialogMessage';
//API import
import {
  getWeatherSearch,
  putCompositionsSites,
  getCompositionsSitesBySiteID,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {format, pullDownMenu} from '../../define';

/**
 * サイト情報の編集
 * @param props props
 * @returns JSX
 */
const SiteEditDialog = (props: {
  displaySiteInfo: apiIF.reqParameters.putCompositionsSites;
  setSiteInfo: React.Dispatch<
    React.SetStateAction<apiIF.reqParameters.putCompositionsSites | null>
  >;
  isOpenDialog: boolean;
  setIsOpenDialog: (arg0: boolean) => void;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID} = useParams();

  //編集前のサイトの情報を取得
  const tempSiteName =
    props.displaySiteInfo['site-name'] === undefined
      ? ''
      : props.displaySiteInfo['site-name'];
  const tempCity =
    props.displaySiteInfo['city'] === undefined
      ? ''
      : props.displaySiteInfo['city'];
  const tempLocation =
    props.displaySiteInfo['location'] === undefined
      ? ''
      : props.displaySiteInfo['location'];
  const tempTimeZone =
    props.displaySiteInfo['timezone'] === undefined
      ? ''
      : props.displaySiteInfo['timezone'];
  const tempAboveGround =
    props.displaySiteInfo['above-ground'] === undefined
      ? '0'
      : props.displaySiteInfo['above-ground'];
  const tempUnderGround =
    props.displaySiteInfo['under-ground'] === undefined
      ? '0'
      : props.displaySiteInfo['under-ground'];

  //prettier-ignore
  //サイト名
  const [siteName, setSiteName] = useState<apiIF.reqParameters.putCompositionsSites['site-name']>(tempSiteName);
  //prettier-ignore
  //市
  const [city, setCity] = useState<apiIF.reqParameters.putCompositionsSites['city']>(tempCity);
  //prettier-ignore
  //天気予報用地域（保存用）
  const [locationSave, setLocationSave] = useState<apiIF.reqParameters.putCompositionsSites['location']>(tempLocation);
  //prettier-ignore
  //天気予報用地域（表示用）
  const [locationDisplay, setLocationDisplay] = useState<apiIF.resParameters.locationInformations | null>(null);
  //prettier-ignore
  //タイムゾーン
  const [timezone, setTimezone] = useState<apiIF.reqParameters.putCompositionsSites['timezone'] | null>(tempTimeZone);
  //prettier-ignore
  //地上階
  const [aboveground, setAboveground] = useState<apiIF.reqParameters.putCompositionsSites['above-ground']>(tempAboveGround);
  //prettier-ignore
  //地下階
  const [underground, setUnderground] = useState<apiIF.reqParameters.putCompositionsSites['under-ground']>(tempUnderGround);
  const [serviceHistory, setServiceHistory] = useState(''); //サービス履歴（この画面では編集しない）
  const [saveBtnDisabled, setSaveBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [isOpenSaveDialog, setIsOpenSaveDialog] = useState(false); //保存ダイアログ表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [siteNameError, setSiteNameError] = useState(false); //サイト名
  const [cityError, setCityError] = useState(false); //市

  const timeZoneList = listTimeZones(); //タイムゾーン一覧

  // サイト情報の更新
  useEffect(() => {
    if (props.displaySiteInfo === undefined) return;

    setSiteName(props.displaySiteInfo['site-name']);
    setCity(props.displaySiteInfo['city']);
    setLocationSave(props.displaySiteInfo['location']);
    setTimezone(props.displaySiteInfo['timezone']);
    setAboveground(props.displaySiteInfo['above-ground']);
    setUnderground(props.displaySiteInfo['under-ground']);
    setServiceHistory(props.displaySiteInfo['service-history']);

    //天気予報用地域を表示用の値を検索
    getWeatherSearch({query: locationSave}).then(result => {
      setLocationDisplay(
        result['search-result'].length > 0 ? result['search-result'][0] : null
      );
    });
  }, [props.displaySiteInfo]);

  //入力項目のチェック
  useEffect(() => {
    if (
      siteName === '' ||
      city === '' ||
      locationSave === '' ||
      timezone === null ||
      siteNameError ||
      cityError
    ) {
      setSaveBtnDisabled(true);
    } else {
      setSaveBtnDisabled(false);
    }
  }, [siteName, city, locationSave, timezone, siteNameError, cityError]);

  /**
   * SAVEボタン押下時処理
   */
  const handleClickOpen = () => {
    setIsOpenSaveDialog(true);
  };

  /**
   * SAVEボタン出現処理
   * @returns JSX
   */
  const SaveButton = (props: {
    siteID: string;
    setSiteInfo: React.Dispatch<
      React.SetStateAction<apiIF.reqParameters.putCompositionsSites | null>
    >;
    setIsOpenDialog: (arg0: boolean) => void;
  }) => {
    //APIの送信データにサイトの情報を追加する
    //送信データ
    const editData: apiIF.reqParameters.putCompositionsSites = {
      'site-name': siteName,
      'city': city,
      'location': locationSave,
      'timezone': timezone === null ? '' : timezone,
      'above-ground': aboveground,
      'under-ground': underground,
      'service-history': serviceHistory,
    };

    return (
      <>
        {/* 入力必須箇所に空欄が有る場合はSAVEボタンをDisabled */}
        <PrimaryButton
          onClick={handleClickOpen}
          buttonText={trans('SiteEditDialog.buttonTxt.save')}
          id={`mv-btnOkSiteSettingEdit${tempSiteName}`}
          disabled={saveBtnDisabled}
          className="siteSetting__button--save"
        />
        {/* SAVE押下時、Dialogで実行の最終確認をする */}
        <DialogPage
          open={isOpenSaveDialog}
          setOpen={setIsOpenSaveDialog}
          titleString={trans('SiteSetting.dialogBox.save.title')}
          contentText={trans('SiteSetting.dialogBox.save.msg')}
          buttonNameLeft={trans('SiteSetting.dialogBox.buttonTxt.cancel')}
          buttonNameRight={trans('SiteSetting.dialogBox.buttonTxt.ok')}
          execFunc={() =>
            putCompositionsSites(props.siteID, editData)
              .then(() => {
                //修正した情報を取得
                return getCompositionsSitesBySiteID(props.siteID);
              })
              .then(response => {
                props.setSiteInfo(() => response);
                setIsOpenSaveDialog(false);
                props.setIsOpenDialog(false);
              })
              .catch(() => {
                setIsOpenErrorDialog(true);
              })
              .finally(() => {
                setAPIWait(false);
              })
          }
          redirectPath={''}
          id="mv-siteSettingChg"
        />
      </>
    );
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setSiteName(tempSiteName);
    setCity(tempCity);
    setLocationSave(tempLocation);
    setTimezone(tempTimeZone);
    setAboveground(tempAboveGround);
    setUnderground(tempUnderGround);
    props.setIsOpenDialog(false);
  };

  //パスパラメータを取得できない場合
  if (siteID === undefined) return <></>;
  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">
          {trans('SiteEditDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Box className="siteSetting__text--required">
              {trans('SiteEditDialog.note')}
            </Box>
            {/** サイトの名前 */}
            <ChkInputTextbox
              setValue={setSiteName}
              setError={setSiteNameError}
              chkLength={format.BASIC_LENGTH_MAX}
              type="text"
              label={`${trans('SiteEditDialog.txtField.label.siteName')}*`}
              value={siteName}
              id="mv-inputEditSiteName"
              class="siteSetting__textBox--freeItem"
              helperText=" "
              errHelperText={trans('SiteEditDialog.txtField.errHelperTxt')}
              variant="standard"
              autoFocus={true}
              margin="dense"
              chkInputProhibitedCharacter={format.BASIC_PATTERN_S3_POLICY}
            />
            {/** 市 */}
            <ChkInputTextbox
              setValue={setCity}
              setError={setCityError}
              chkLength={format.BASIC_LENGTH_MAX}
              type="text"
              label={`${trans('SiteEditDialog.txtField.label.city')}*`}
              value={city}
              id="mv-editCityName"
              class="siteSetting__textBox--freeItem"
              helperText=" "
              errHelperText={trans('SiteEditDialog.txtField.errHelperTxt')}
              variant="standard"
            />
            {/** 天気予報用地域 */}
            <FindLocation
              id="mv-editLocationName"
              class="siteSetting__textBox--pulldown"
              setLocation={setLocationSave}
              locationData={locationDisplay}
            />
            {/** タイムゾーン */}
            <ComboBox
              data={timeZoneList}
              setValue={setTimezone}
              value={timezone}
              id="mv-editTimezone"
              class="siteSetting__textBox--pulldown"
            />
            {/** 地上階 */}
            <TextFieldSelectBox
              label={`${trans('SiteEditDialog.txtField.label.aboveground')}*`}
              choices={pullDownMenu.ABOVEGROUND_FLOOR}
              value={aboveground}
              setValue={setAboveground}
              id="mv-editAbovegndSelect"
              margin="normal"
              class="siteSetting__textBox--pulldownFloor"
            />
            {/** 地下階 */}
            <TextFieldSelectBox
              label={`${trans('SiteEditDialog.txtField.label.basement')}*`}
              choices={pullDownMenu.UNDERGROUND_FLOOR}
              value={underground}
              setValue={setUnderground}
              id="mv-editUndergndSelect"
              margin="normal"
              class="siteSetting__textBox--pulldownFloor"
            />
            {/* 入力必須箇所に空欄が有る場合は、警告文を表示 */}
            {saveBtnDisabled ? (
              <Box className="siteSetting__text--error">
                {trans('SiteEditDialog.errMsg.inputErr')}
              </Box>
            ) : (
              <></>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {/* SAVE/CANCELボタンの設定） */}
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('SiteEditDialog.buttonTxt.cancel')}
            id={`mv-btnCancelSiteSettingEdit${tempSiteName}`}
          />
          <SaveButton
            siteID={siteID}
            setSiteInfo={props.setSiteInfo}
            setIsOpenDialog={props.setIsOpenDialog}
          />
        </DialogActions>
      </Dialog>
      {/* エラー表示 */}
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={`${tempSiteName}EditErrDlgBtn`}
      />
    </>
  );
};
export default SiteEditDialog;
